import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { PayoutStatus } from "@/gql/graphql";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ProcessDriverPayoutDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProcessDriverPayoutDialog";
import { EditPendingPayoutDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/EditPendingPayoutDialog";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  deleteWorkOrderAssignmentPayout,
  resendPayoutEmail,
  updateWorkOrderAssignmentPayout,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderPayoutMutations";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { toast } from "sonner";
import { TrashIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import {
  ArchiveBoxIcon,
  PencilSquareIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { PDFIcon } from "@/assets/Icons";

export type PayoutAction = {
  id: string;
  status: PayoutStatus;
  invoice_url: string;
};

export function useWorkOrderAssignmentPayoutActions(): ActionsHook<PayoutAction> {
  const { open: openProcess } = useDialog(ProcessDriverPayoutDialog);
  const { open: openEdit } = useDialog(EditPendingPayoutDialog);
  const { open: openPdf } = useDialog(PreviewPdfDialog);
  const { open: openConfirmDelete } = useConfirmDeleteDialog();
  const { mutateAsync: deletePayout } = useGqlMutation(
    deleteWorkOrderAssignmentPayout,
  );
  const { mutateAsync: updatePayout } = useGqlMutation(
    updateWorkOrderAssignmentPayout,
  );
  const { mutateAsync: resendEmail } = useGqlMutation(resendPayoutEmail);

  return (payout) => {
    const isPending = payout.status === PayoutStatus.Pending;
    const isPaid = payout.status === PayoutStatus.Paid;

    return [
      {
        label: "Actions",
        items: [
          {
            actions: [
              {
                isHidden: !isPending,
                label: "Process",
                Icon: PlayIcon,
                handleAction: () => {
                  openProcess({ payoutId: payout.id });
                },
              },
              {
                isHidden: !isPending,
                label: "Edit line items",
                Icon: PencilSquareIcon,
                handleAction: () => {
                  openEdit({ payoutId: payout.id });
                },
              },
              {
                label: "View PDF",
                Icon: PDFIcon,
                handleAction: () => {
                  openPdf({ url: payout.invoice_url });
                },
              },
              {
                isHidden: !isPaid,
                label: "Resend email",
                Icon: EnvelopeIcon,
                handleAction: async () => {
                  await resendEmail({ id: payout.id });
                  toast.success("Payment email sent");
                },
              },
            ],
          },
          {
            actions: [
              {
                color: "red",
                isHidden: !isPaid,
                label: "Void",
                Icon: ArchiveBoxIcon,
                handleAction: () => {
                  openConfirmDelete({
                    title: "Void Payout",
                    message:
                      "Are you sure you want to void this payout? This will reverse the payment record.",
                    buttonText: "Void",
                    onDelete: async () => {
                      await updatePayout({
                        id: payout.id,
                        input: { status: PayoutStatus.Voided },
                      });
                      toast.success("Payout voided");
                    },
                  });
                },
              },
              {
                color: "red",
                isHidden: !isPending,
                label: "Delete",
                Icon: TrashIcon,
                handleAction: () => {
                  openConfirmDelete({
                    title: "Delete Payout",
                    message:
                      "Are you sure you want to delete this pending payout?",
                    onDelete: async () => {
                      await deletePayout({ id: payout.id });
                      toast.success("Payout deleted");
                    },
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
